// src/encounter/generated-note-body.tsx
// The generated note — one container, used by the in-encounter drawer's Note tab and by the
// pre-encounter rail's Note tab (PROD-1999).
//
// Why it is shared rather than written twice: the note is the same artefact on both surfaces, and
// Flutter builds it from ONE widget (`GeneratedNotesWidget`) mounted in `sidePanel()`
// (encounter_screen.dart:1657) beside both the pre-encounter columns and the started encounter.
//
// Since 2026-09-23 everything visual is the Signal DS `GeneratedNote` organism, graduated from
// this app's own note column: the fixed six-section list (HPI · Risk · ROS · PE · Docs · A/P) with
// Flutter's empty states and CTA, the tile headers, the key/value tables, the 82px tail, the
// stray-section fallback and the scroll-follow (`activeStep`, 30ms, skips wrap-up). THE NOTE IS
// CUMULATIVE — the active step only scrolls, it never filters (Andrew, 2026-08-21).
//
// What stays here is data shaping the DS deliberately does not know about:
//   · the VITALS and PATIENT SUMMARY preamble arrive as ordinary `NoteSection`s
//     (`lib/to-note-preamble.ts`) and are lifted out into the organism's own `vitals` /
//     `patientSummary` props, because Flutter draws them as tables ABOVE the section list;
//   · the Docs rows are READ here (`note-column/note-docs-feed.tsx`), so the query mounts only
//     where the note renders.
import type { ReactNode } from 'react';
import {
  CARE_PATHWAY_NOTE_INSET,
  GeneratedNote,
  type GeneratedNoteApRow,
  type GeneratedNoteApSection,
  type GeneratedNoteDoc,
  type GeneratedNotePatient,
  type GeneratedNoteSectionKey,
  type GeneratedNoteVitals,
} from '@scripsteam/scrips-signal-ds';
import { AP_NOTE_TITLE, type NoteSection } from './generated-note';
import { VITALS_NOTE_TITLE, PATIENT_SUMMARY_NOTE_TITLE } from './lib/to-note-preamble';
import type { NoteComplaints } from './note-column/to-note-complaints';
import { NoteDocsFeed } from './note-column/note-docs-feed';

/** One A/P row — the DS type, under the name the drawer and `lib/to-ap-note.ts` import. */
export type ApNoteRow = GeneratedNoteApRow;
/** A grouped A/P section (`'diagnosis'` · `'order'` · `'notes'`) — the DS type. */
export type ApNoteSection = GeneratedNoteApSection;
/** One Docs row (`DocumentRowWidget`) — the DS type. */
export type NoteDocRow = GeneratedNoteDoc;
/** A note section's identity — the key the empty-state CTA routes. */
export type NoteSectionKey = GeneratedNoteSectionKey;

export interface GeneratedNoteBodyProps {
  /** Identity stamp — omitted entirely when absent (no placeholder identity on a clinical note). */
  patient?: GeneratedNotePatient;
  reasonForVisit?: string;
  /** Questionnaire-derived sections (Vitals · Patient Summary · HPI · Risk · ROS · PE). */
  noteSections?: NoteSection[];
  /** A/P's grouped coded sections. Rendered IN ADDITION to the above, never instead of them. */
  apNoteSections?: ApNoteSection[];
  /**
   * The encounter's documents. `undefined` → Docs shows its empty state without counting as
   * content; an EMPTY ARRAY is the honest "asked, and there are none".
   */
  docs?: NoteDocRow[];
  /**
   * Instead of `docs`, the ids to READ the documents with (see `note-column/note-docs-feed.tsx`
   * for why the fetch mounts here and not in the page). Both ids required.
   */
  docsPatientId?: string | null;
  docsEncounterId?: string | null;
  /**
   * The SAVED chief complaints — the same `usePatientComplaint` record the HPI step reads. Flutter's
   * CC note section is sourced from the complaints, not the questionnaire
   * (`chief_complaint_note_component_widget.dart.dart:52-53`).
   */
  chiefComplaints?: NoteComplaints;
  /**
   * `enableAction` — `notes_component_widget.dart:130`. Gates the empty-state CTA. DEFAULT FALSE,
   * which is what keeps the PROD-2011 guard honest: no host gets a CTA unless it opts in.
   */
  canStartEncounter?: boolean;
  /** `isAppointmentStarted` — drops the "Start Appointment and " prefix from the CTA (`:132`). */
  isAppointmentStarted?: boolean;
  /** Fired by the empty-state CTA. */
  onGoToSection?: (key: NoteSectionKey) => void;
  /** Ambient scribe panel, above the note: the thing just done, above the standing record. */
  ambient?: ReactNode;
  /**
   * Scroll-follow: the step the clinician is standing on (generated_notes_widget.dart:194-217).
   * Omitted → no scrolling, which is right for the pre-encounter rail.
   */
  activeStep?: string;
}

export function GeneratedNoteBody({
  noteSections,
  apNoteSections,
  docs,
  docsPatientId,
  docsEncounterId,
  ...note
}: GeneratedNoteBodyProps) {
  const all = noteSections ?? [];
  const vitalsSection = all.find((s) => s.section === VITALS_NOTE_TITLE);
  const summarySection = all.find((s) => s.section === PATIENT_SUMMARY_NOTE_TITLE);
  const sections = all.filter(
    (s) => s.section !== VITALS_NOTE_TITLE && s.section !== PATIENT_SUMMARY_NOTE_TITLE,
  );
  const vitals = vitalsSection ? toVitalsBlock(vitalsSection) : undefined;
  const patientSummary = summarySection
    ? (summarySection.categories ?? []).filter((c) => c.lines.some((l) => l.trim().length > 0))
    : undefined;

  /*
   * The one case that renders NO sections: a bare host with nothing fed. The organism then shows
   * "No responses yet." — but only if the Docs feed does not run, because a fetched `[]` would
   * count as content and draw six empty sections against an unmounted encounter. So the feed
   * mounts only once the note has something of its own (`encounter-drawer.test.tsx` pins the
   * wholly-empty shape).
   */
  const hasAnything =
    all.length > 0 ||
    (apNoteSections?.length ?? 0) > 0 ||
    Boolean(docs) ||
    Boolean(note.patient || note.reasonForVisit) ||
    (note.chiefComplaints?.rows.length ?? 0) > 0 ||
    Boolean(note.chiefComplaints?.note);
  if (!hasAnything) return <GeneratedNote {...note} />;

  return (
    <NoteDocsFeed patientId={docsPatientId} encounterId={docsEncounterId}>
      {(fetched) => (
        <GeneratedNote
          {...note}
          vitals={vitals}
          patientSummary={patientSummary}
          sections={sections}
          apSections={apNoteSections}
          docs={docs ?? fetched}
        />
      )}
    </NoteDocsFeed>
  );
}

/**
 * The VITALS table + its review line. `vitalsNoteSection` emits one category per vital plus a
 * LAST, label-less category carrying the review sentence (`to-note-preamble.ts`) — Flutter draws
 * that sentence under the table, not as a keyed row (`vital_component_widget.dart:56-58`).
 *
 * The empty-value filter is Flutter's own (`vitals_note_component_widget.dart:27-31`) and very
 * nearly a no-op with our data: `display()` returns an em-dash for a missing reading, which Flutter
 * keeps too (Andrew's 2026-08-24 Flutter screenshot).
 */
function toVitalsBlock(section: NoteSection): GeneratedNoteVitals {
  const cats = section.categories ?? [];
  const reviewLine = cats.find((c) => c.title === '')?.lines[0];
  const rows = cats.filter((c) => c.title !== '' && c.lines.some((l) => l.trim().length > 0));
  return { rows, reviewLine };
}

/**
 * Flutter's generated-note ListView inset (`generated_notes_widget.dart:101-102`), applied by each
 * HOST's note container (the drawer and the pre-encounter rail) because it also covers the note's
 * siblings there — the signed attestation and the footer. The value is the Signal DS
 * `CARE_PATHWAY_NOTE_INSET`, so both hosts read one constant.
 */
export const NOTE_INSET = CARE_PATHWAY_NOTE_INSET;

export { AP_NOTE_TITLE };
